// Consola Operadora V1 - Componente React independiente
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OperatorCart, OperatorItem, loadOperatorCart, updateOperatorItems, ACTION_LABELS, ACTION_CONFIRM } from './operatorConsole';
import * as api from '../../services/api';
import socketOperator from '../../services/socketOperator';

interface Props {
  cartId: string;
  onBack?: () => void;
  onGoToStore?: () => void;
  onProductsUpdated?: () => void;
  virtualCart?: { items: { productCode: string; name: string; price: number; status: string; image?: string }[] };
  // KF25-UX-001A: cuando el contenedor (AdminCartView/AdminOpView) ya monta la
  // cabecera canónica única (Panel/Tienda/Sesión), la cabecera embebida se oculta
  // para eliminar la doble fila de navegación. Los props onBack/onGoToStore se
  // conservan para el estado "Carrito no encontrado" (navegación SPA intacta).
  hideNavHeader?: boolean;
}

export const OperatorConsole: React.FC<Props> = ({ cartId, onBack, onGoToStore, onProductsUpdated, virtualCart, hideNavHeader }) => {
  const [cart, setCart] = useState<OperatorCart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // UX-001D: selección batch persistida en sessionStorage (contexto de trabajo;
  // se limpia en Cerrar sesión). Mismo patrón arquitectónico que useViewMemory
  // (App.tsx): ref-mirror + escritura SÍNCRONA en el setter — la persistencia
  // no depende de efectos ni del ciclo de render (una navegación en el mismo
  // batch descartaría el updater de React).
  const selectedRef = useRef<Set<string>>(new Set());
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem('kf25_vm_console.selected');
      const v: Set<string> = raw ? new Set(JSON.parse(raw)) : new Set();
      selectedRef.current = v;
      return v;
    } catch { return new Set(); }
  });
  const persistSelected = useCallback((v: any) => {
    const next = typeof v === 'function' ? v(selectedRef.current) : v;
    selectedRef.current = next;
    try { sessionStorage.setItem('kf25_vm_console.selected', JSON.stringify(Array.from(next))); } catch {}
    setSelectedCodes(next);
  }, []);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Cargar carrito (real o virtual)
  const load = useCallback(async () => {
    if (virtualCart) {
      // Carrito virtual — construir desde los datos recibidos
      setCart({
        id: cartId,
        items: virtualCart.items.map((item, idx) => ({
          code: item.productCode,
          name: item.name,
          price: item.price || 0,
          status: item.status as any,
          quantity: 1,
          id: 'virtual_' + item.productCode
        })),
        created_at: new Date().toISOString(),
        status: 'active'
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await loadOperatorCart(cartId);
      if (result) {
        setCart(result);
      } else {
        setError('Carrito no encontrado');
      }
    } catch {
      setError('Error al cargar carrito');
    }
    setLoading(false);
  }, [cartId, virtualCart]);

  useEffect(() => { load(); }, [load]);

  // Realtime Platform: la consola consume el MISMO motor que la Tienda.
  // Eventos de este carrito (acciones de otras operadoras u otros dispositivos)
  // → recarga automática. Sin polling. Resync en reconexión.
  useEffect(() => {
    const relevantEvents = ['item:added', 'item:removed', 'item:sold', 'item:released', 'payment:verifying', 'timer:started', 'timer:expired', 'cart:closed'];
    const unsubs = relevantEvents.map(evt =>
      socketOperator.on(evt, (data: any) => {
        if (data && data.cartId === cartId) {
          load();
          if (onProductsUpdated) onProductsUpdated();
        }
      })
    );
    const unsubResync = socketOperator.onResync(() => { load(); if (onProductsUpdated) onProductsUpdated(); });
    return () => { unsubs.forEach(u => u()); unsubResync(); };
  }, [cartId, load, onProductsUpdated]);

  // Toggle selección
  const toggleCode = (code: string) => {
    persistSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // Items visibles: solo los que requieren acción del operador
  const visibleItems = cart
    ? cart.items.filter(i => i.status === 'pending' || i.status === 'verifying' || i.status === 'in_process')
    : [];

  const toggleAll = () => {
    if (!cart) return;
    if (selectedCodes.size === visibleItems.length) {
      persistSelected(new Set());
    } else {
      persistSelected(new Set(visibleItems.map(i => i.code)));
    }
  };

  // Ejecutar acción en lote
  const executeAction = async (status: string) => {
    if (!cart || selectedCodes.size === 0) return;
    const label = ACTION_LABELS[status] || status;
    const confirmText = ACTION_CONFIRM[status] || status;
    if (!window.confirm(`¿Seguro que quieres ${confirmText} ${selectedCodes.size} producto(s)?`)) return;

    const codes = Array.from(selectedCodes);
    let ok = false;
    if (virtualCart) {
      // Producto independiente — usar endpoint de producto
      const res = await api.updateProductStatus(codes[0], status);
      ok = res && res.success;
      // Liberar también limpia blockedSearchProducts + blockedGlobal
      if (ok && status === 'active') {
        await api.unblockProduct(codes[0]);
      }
    } else {
      // Carrito real — usar endpoint de carrito
      ok = await updateOperatorItems(cart.id, codes, status);
    }
    if (ok) {
      setActionMsg(`${selectedCodes.size} producto(s) actualizados`);
      persistSelected(new Set());
      load();
      if (onProductsUpdated) onProductsUpdated();
      setTimeout(() => setActionMsg(null), 3000);
    } else {
      alert('Error al actualizar productos');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: '#666' }}>Cargando carrito...</div>
      </div>
    );
  }

  if (error || !cart) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Carrito no encontrado</h1>
        <p style={{ color: '#666', marginBottom: '24px' }}>El carrito {cartId} no existe.</p>
        <button onClick={onGoToStore || (() => window.location.href = '/')}
          style={{ padding: '12px 24px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }}>
          Volver a la tienda
        </button>
      </div>
    );
  }

  const statusColor: Record<string, string> = {
    pending: '#f59e0b',
    verifying: '#3b82f6',
    sold: '#22c55e',
    returned: '#6b7280',
    active: '#22c55e',
    in_process: '#7c3aed'
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px', fontFamily: 'sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            {!hideNavHeader && onGoToStore && (
              <button onClick={onGoToStore}
                style={{ padding: '8px 16px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}>
                ← Ir a la tienda
              </button>
            )}
            {!hideNavHeader && onBack && (
              <button onClick={onBack}
                style={{ padding: '8px 16px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}>
                ← Volver a consola
              </button>
            )}
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>🛠️ {cart.id}</h1>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0' }}>
            {cart.items.length} producto(s) • Creado {new Date(cart.created_at).toLocaleString()}
          </p>
        </div>
        {cart.status === 'completed' && (
          <span style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', background: '#d1fae5', color: '#065f46' }}>
            ✅ Completado
          </span>
        )}
      </div>

      {/* Barra de acciones */}
      {selectedCodes.size > 0 && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, color: '#374151' }}>{selectedCodes.size} producto(s) seleccionado(s)</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => executeAction('active')}
              style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              🆓 Liberar
            </button>
            <button onClick={() => executeAction('verifying')}
              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              🔍 Verificación de pago
            </button>
            <button onClick={() => executeAction('sold')}
              style={{ padding: '8px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              ✅ Vendido
            </button>
            <button onClick={async () => {
              const codes = Array.from(selectedCodes);
              if (codes.length === 1) {
                window.open('/export/' + codes[0], '_blank');
              } else {
                window.open('/export/' + codes[0], '_blank');
              }
            }}
              style={{ padding: '8px 16px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              ⬇️ Exportar
            </button>
            <button onClick={async () => {
              const codes = Array.from(selectedCodes);
              if (!window.confirm(`¿Ocultar ${codes.length} producto(s) del feed público?`)) return;
              for (const code of codes) {
                await api.hideProduct(code);
              }
              setActionMsg(`${codes.length} producto(s) ocultados`);
              persistSelected(new Set());
              if (onProductsUpdated) onProductsUpdated();
              setTimeout(() => setActionMsg(null), 3000);
            }}
              style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              ◐ Ocultar
            </button>
          </div>
        </div>
      )}

      {/* Mensaje de acción */}
      {actionMsg && (
        <div style={{ background: '#d1fae5', border: '1px solid #86efac', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#065f46', fontWeight: 500, textAlign: 'center' }}>
          {actionMsg}
        </div>
      )}

      {/* Lista de productos con selector */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center' }}>
          <input type="checkbox"
            checked={visibleItems.length > 0 && selectedCodes.size === visibleItems.length}
            onChange={toggleAll}
            style={{ width: '18px', height: '18px', marginRight: '12px', accentColor: '#7c3aed' }} />
          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
            {selectedCodes.size === 0
              ? 'Seleccionar todos'
              : `${selectedCodes.size} de ${visibleItems.length} seleccionados`
            }
          </span>
        </div>

        <div style={{ borderBottom: '1px solid #e5e7eb' }}>
          {visibleItems.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No hay productos pendientes de gestión
            </div>
         ) : visibleItems.map(item => {
            const isSelected = selectedCodes.has(item.code);
            return (
              <div key={item.code}
                style={{
                  display: 'flex', alignItems: 'center', padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  background: isSelected ? '#ede9fe' : 'white',
                  borderLeft: isSelected ? '4px solid #7c3aed' : '4px solid transparent',
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s'
                }}
                onClick={() => toggleCode(item.code)}>
                <input type="checkbox" checked={isSelected} readOnly
                  style={{ width: '18px', height: '18px', marginRight: '12px', accentColor: '#7c3aed' }} />
                <div style={{ width: '56px', height: '56px', marginRight: '12px', borderRadius: '8px', background: '#f3f4f6', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.image
                    ? (/\.(mp4|mov|webm)$/i.test(item.image)
                        // KF25-UX-001C: thumbnail type-aware (video-primarios — primer frame como preview)
                        ? <video src={item.image} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        : <img src={item.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />)
                    : <span style={{ fontSize: '20px', color: '#9ca3af' }}>📷</span>
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{item.name}</div>
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>{item.code}</div>
                </div>
                <div style={{ textAlign: 'right', marginRight: '12px' }}>
                  <div style={{ fontWeight: 700, color: '#111827' }}>RD$ {item.price.toLocaleString()}</div>
                </div>
                <span style={{
                  padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                  background: statusColor[item.status] ? `${statusColor[item.status]}15` : '#f3f4f6',
                  color: statusColor[item.status] || '#374151'
                }}>
                  {item.status === 'pending' ? 'Pendiente' :
                   item.status === 'verifying' ? 'Verificando pago' :
                   item.status === 'in_process' ? 'En proceso' :
                   item.status === 'sold' ? 'Vendido' :
                   item.status === 'returned' ? 'Devuelto' :
                   item.status === 'active' ? 'Activo' : item.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumen */}
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', background: '#f9fafb', borderRadius: '12px', padding: '16px', border: '1px solid #e5e7eb' }}>
        <span style={{ fontWeight: 600, color: '#374151' }}>Total</span>
        <span style={{ fontWeight: 700, fontSize: '20px', color: '#7c3aed' }}>
          RD$ {visibleItems.reduce((s, i) => s + i.price, 0).toLocaleString()}
        </span>
      </div>
    </div>
  );
};
